import { useTranslate } from "@opensea/next-translate"
import { FlexColumn } from "@opensea/ui-kit"
import { AuthCode } from "@opensea/ui-kit/client"
import {
  errorIndicatesMaxMfaRetries,
  errorIndicatesMfaTimeout,
  errorIndicatesMfaVerificationFailed,
  useMfaEnrollment,
} from "@privy-io/react-auth"
import React, { useState } from "react"
import { captureException } from "@sentry/nextjs"
import { Button } from "@/design-system/Button"
import { Text } from "@/design-system/Text"
import { Tooltip } from "@/design-system/Tooltip"
import type { MfaError } from "@/providers/Privy/auth/MfaRequestHandler/useMfaErrorText"
import { useMfaErrorText } from "@/providers/Privy/auth/MfaRequestHandler/useMfaErrorText"
import {
  RESEND_CODE_TIMEOUT_SECONDS,
  useResendAuthCode,
} from "@/providers/Privy/auth/MfaRequestHandler/useResendAuthCode"

type Props = {
  phoneNumber: string
  onVerified: () => void
}

export function ManageSmsCodeStep({ phoneNumber, onVerified }: Props) {
  const t = useTranslate("mfa")
  const { initEnrollmentWithSms, submitEnrollmentWithSms } = useMfaEnrollment()
  const { canResendCode, resetCanResendCode } = useResendAuthCode()
  const [error, setError] = useState<MfaError>()
  const errorText = useMfaErrorText(error)

  const handleAuthCode = async (code: string) => {
    try {
      await submitEnrollmentWithSms({
        phoneNumber,
        mfaCode: code,
      })
      onVerified()
    } catch (e) {
      if (errorIndicatesMfaVerificationFailed(e)) {
        setError("verificationFailed")
      } else if (errorIndicatesMaxMfaRetries(e)) {
        setError("maxAttemptsReached")
        resetCanResendCode()
      } else if (errorIndicatesMfaTimeout(e)) {
        setError("timeout")
      } else if (
        (e as Error).message === "Request failed with status code 400"
      ) {
        // likely (but maybe not guaranteed) to be this error
        setError("alreadyLinked")
      } else {
        setError("unknown")
        // since this shouldn't happen, log the error
        captureException(e, {
          extra: { canResendCode },
          tags: { userFlow: "privy" },
        })
      }
    }
  }

  const handleResendCode = async () => {
    resetCanResendCode()
    setError(undefined)
    try {
      await initEnrollmentWithSms({
        phoneNumber,
      })
    } catch (e) {
      captureException(e, {
        extra: { canResendCode },
      })
    }
  }

  return (
    <FlexColumn className="w-full grow items-center justify-between">
      <FlexColumn className="items-center gap-2">
        <AuthCode
          error={Boolean(error)}
          onComplete={handleAuthCode}
          overrides={{
            Inputs: {
              className: "bg-component-gray-1 dark:bg-gray-3",
            },
          }}
        />
        {error ? (
          <Text color="error" size="small">
            {errorText}
          </Text>
        ) : null}
      </FlexColumn>
      <Tooltip
        content={
          error === "maxAttemptsReached" || error === "timeout"
            ? undefined
            : t(
                "enrollment.step.resendTooltip",
                {
                  one: "If no text message within {{count}} second, click to resend.",
                  other:
                    "If no text message within {{count}} seconds, click to resend.",
                },
                { count: RESEND_CODE_TIMEOUT_SECONDS },
              )
        }
        variant="card"
      >
        <Button
          className="w-full"
          disabled={!canResendCode}
          onClick={handleResendCode}
          variant="secondary"
        >
          {t("enrollment.step.resend", "Resend authentication")}
        </Button>
      </Tooltip>
    </FlexColumn>
  )
}
